import { useTranslation } from 'react-i18next'
import { PlusIcon, Trash2Icon } from 'lucide-react'
import {
	Button,
	Checkbox,
	IconButton,
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@rentflowdev/ui'
import { DEFAULT_LOCALE } from '@/types'
import type { SubjectServiceRow } from './types'

function formatNumber(n: number): string {
	if (!Number.isFinite(n)) return '—'
	return n.toLocaleString(DEFAULT_LOCALE)
}

function parseNumberInput(raw: string): number {
	const n = parseFloat(raw.replace(',', '.'))
	return Number.isFinite(n) ? n : 0
}

function makeEmptyRow(): SubjectServiceRow {
	return {
		id:        crypto.randomUUID(),
		selected:  true,
		name:      '',
		qty:       1,
		unit:      'шт',
		unitPrice: 0,
		vatRate:   20,
	}
}

const cellInputClass =
	'w-full rounded-(--radius-sm) bg-transparent px-2 py-1 text-sm leading-5 text-(--color-foreground) placeholder:text-(--color-muted-foreground) focus:bg-(--color-muted)/40 focus:outline-none read-only:text-(--color-muted-foreground) read-only:cursor-default'

interface SubjectServicesTableProps {
	services: SubjectServiceRow[]
	editable: boolean
	onChange: (rows: SubjectServiceRow[]) => void
}

export function SubjectServicesTable({ services, editable, onChange }: SubjectServicesTableProps) {
	const { t } = useTranslation()

	function updateRow<K extends keyof SubjectServiceRow>(id: string, key: K, value: SubjectServiceRow[K]) {
		onChange(services.map((s) => (s.id === id ? { ...s, [key]: value } : s)))
	}

	function addRow() {
		onChange([...services, makeEmptyRow()])
	}

	function removeRow(id: string) {
		onChange(services.filter((s) => s.id !== id))
	}

	// Totals computed over selected rows only. Unchecked rows are excluded from the PDF payload.
	// Prices are VAT-inclusive (RU rental-contract convention).
	const total    = services.filter((s) => s.selected).reduce((acc, s) => acc + s.qty * s.unitPrice, 0)
	const totalVat = services.filter((s) => s.selected).reduce((acc, s) => {
		const rowSum = s.qty * s.unitPrice
		return acc + (rowSum * s.vatRate) / (100 + s.vatRate)
	}, 0)

	return (
		<div className="flex flex-col gap-2">
			<div className="overflow-hidden rounded-(--radius-md) border border-(--color-border) bg-(--color-background)">
				<Table className="table-fixed">
					<TableHeader>
						<TableRow className="hover:bg-transparent">
							<TableHead className="w-[48px]" />
							<TableHead className="w-[52px] text-(--color-muted-foreground)">
								{t('deals.documents.wizard.subject.tableIndex')}
							</TableHead>
							<TableHead>{t('deals.documents.wizard.subject.tableName')}</TableHead>
							<TableHead className="w-[96px]">{t('deals.documents.wizard.subject.tableQty')}</TableHead>
							<TableHead className="w-[96px]">{t('deals.documents.wizard.subject.tableUnit')}</TableHead>
							<TableHead className="w-[96px] text-right">{t('deals.documents.wizard.subject.tablePrice')}</TableHead>
							<TableHead className="w-[96px] text-right">{t('deals.documents.wizard.subject.tableSum')}</TableHead>
							{editable && <TableHead className="w-[44px]" />}
						</TableRow>
					</TableHeader>
					<TableBody>
						{services.map((s, idx) => (
							<TableRow key={s.id}>
								<TableCell className="px-4 py-3">
									<Checkbox
										checked={s.selected}
										onCheckedChange={(v) => updateRow(s.id, 'selected', v === true)}
										aria-label={t('deals.documents.wizard.subject.selectService')}
										disabled={!editable}
									/>
								</TableCell>
								<TableCell className="px-2 py-3 text-sm text-(--color-foreground)">{idx + 1}</TableCell>
								<TableCell className="px-2 py-2">
									<input
										type="text"
										value={s.name}
										onChange={(e) => updateRow(s.id, 'name', e.target.value)}
										placeholder={t('deals.documents.wizard.subject.servicePlaceholder')}
										readOnly={!editable}
										className={cellInputClass}
									/>
								</TableCell>
								<TableCell className="px-2 py-2">
									<input
										type="text"
										inputMode="decimal"
										value={String(s.qty)}
										onChange={(e) => updateRow(s.id, 'qty', parseNumberInput(e.target.value))}
										readOnly={!editable}
										className={cellInputClass}
									/>
								</TableCell>
								<TableCell className="px-2 py-2">
									<input
										type="text"
										value={s.unit}
										onChange={(e) => updateRow(s.id, 'unit', e.target.value)}
										readOnly={!editable}
										className={cellInputClass}
									/>
								</TableCell>
								<TableCell className="px-2 py-2 text-right">
									<input
										type="text"
										inputMode="decimal"
										value={String(s.unitPrice)}
										onChange={(e) => updateRow(s.id, 'unitPrice', parseNumberInput(e.target.value))}
										readOnly={!editable}
										className={`${cellInputClass} text-right`}
									/>
								</TableCell>
								<TableCell className="px-4 py-3 text-right text-sm text-(--color-foreground)">
									{formatNumber(s.qty * s.unitPrice)}
								</TableCell>
								{editable && (
									<TableCell className="px-2 py-2">
										<IconButton
											type="button"
											variant="ghost"
											size="sm"
											aria-label={t('deals.documents.wizard.subject.removeService')}
											onClick={() => removeRow(s.id)}
										>
											<Trash2Icon className="size-3.5" />
										</IconButton>
									</TableCell>
								)}
							</TableRow>
						))}
						<TableRow className="bg-(--color-muted)/30 hover:bg-(--color-muted)/30">
							<TableCell colSpan={5} className="px-4 py-3 align-top">
								<div className="flex flex-col">
									<span className="text-sm font-[500] leading-5 text-(--color-foreground)">
										{t('deals.documents.wizard.subject.total')}
									</span>
									<span className="text-sm leading-5 text-(--color-muted-foreground)">
										{t('deals.documents.wizard.subject.totalVat')}
									</span>
								</div>
							</TableCell>
							<TableCell colSpan={editable ? 3 : 2} className="px-4 py-3 text-right align-top">
								<div className="flex flex-col items-end">
									<span className="text-sm font-[500] leading-5 text-(--color-foreground)">{formatNumber(total)}</span>
									<span className="text-sm leading-5 text-(--color-muted-foreground)">{formatNumber(Math.round(totalVat))}</span>
								</div>
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</div>

			{editable && (
				<Button
					type="button"
					variant="outline"
					size="full"
					iconLeft={<PlusIcon className="size-4" />}
					onClick={addRow}
				>
					{t('deals.documents.wizard.subject.addService')}
				</Button>
			)}
		</div>
	)
}
